import '@/styles/globals.css'

import type { FC, ReactNode } from 'react'
import type { AppProps } from 'next/app'
import type { Locale } from '@/constants/i18n'

import Head from 'next/head'
import { useEffect } from 'react'
import { Cormorant_Garamond, Geist_Mono, IBM_Plex_Sans_Arabic, Manrope } from 'next/font/google'
import { I18nProvider } from '@/i18n/context'
import { isLocale, localeDir } from '@/constants/i18n'

const sans = Manrope({
    subsets: ['latin', 'latin-ext']
})

const heading = Cormorant_Garamond({
    subsets: ['latin', 'latin-ext'],
    weight: ['500', '600', '700']
})

const arabic = IBM_Plex_Sans_Arabic({
    subsets: ['arabic'],
    weight: ['400', '500', '600', '700']
})

const mono = Geist_Mono({
    subsets: ['latin']
})

const fontVariables = `:root{--font-app-sans:${sans.style.fontFamily};--font-app-mono:${mono.style.fontFamily};--font-app-heading:${heading.style.fontFamily};--font-app-arabic:${arabic.style.fontFamily}}`

const App: FC<AppProps> = ({ Component, pageProps, router }): ReactNode => {
    const isAdmin = router.pathname.startsWith('/admin')
    const locale: Locale = isAdmin ? 'de' : isLocale(pageProps?.locale) ? pageProps.locale : 'en'
    const dir = isAdmin ? 'ltr' : localeDir(locale)

    useEffect(() => {
        document.documentElement.lang = locale
        document.documentElement.dir = dir
    }, [locale, dir])

    return (
        <div lang={locale} dir={dir} className='min-h-full font-sans'>
            <Head>
                <style dangerouslySetInnerHTML={{ __html: fontVariables }} />
            </Head>

            <I18nProvider locale={locale}>
                <Component {...pageProps} />
            </I18nProvider>
        </div>
    )
}

export default App
